import {
  DocumentCriterion,
  Flags,
  SearchOperator as Op,
  SearchOperator,
} from '@fullstory/client-api';
import { assertExhaustive, pick } from '@fullstory/typeutils';
import {
  Browser,
  CustomFieldIdent,
  CustomFieldType,
  DatalossType,
  DependentType,
  Device,
  Duration,
  durationScalarMs,
  DurationUnit,
  ElementValueType,
  ErrorClickType,
  EventCustomErrorKinds,
  EventType,
  EventUrlKinds,
  isDurationValue,
  isTokenCategoryOrNotSet,
  isUrlTypeWithValue,
  Kind,
  NONE,
  OS,
  PageRefValueType,
  PartialDuration,
  PinchGesture,
  Platform,
  SearchFilter,
  SearchType,
  SeenVisibility,
  SessionType,
  UiOnlyKinds,
  UiOnlySubkinds,
  UrlCategory,
  UrlValue,
} from '@fullstory/search-types';
import {
  ApiErrorClientConverter,
  ConverterSpec,
  FieldEventType,
  SubkindConverterMap,
  withValueWrapping,
} from './shared';
import {
  fromMilli,
  fromSecond,
  isValidSolrTime,
  parseSolrTime,
  toMilli,
  toSolrUnit,
} from './duration';
import * as api from '@fullstory/proto/fs/api/segment';
import * as segment from '@fullstory/proto/fs/api/segment';
import * as protobuf from '@fullstory/proto/google/protobuf';
import { VarType } from '@fullstory/search-vars';
import { NamedElementIdField } from '@fullstory/client-api/goimports/fs_search_doc_fields_special';
import { int64 } from '@fullstory/proto/google/protobuf';

type CriteriaAdjuster = (c: DocumentCriterion[]) => DocumentCriterion[];

// convert filter to search type based on naming convention with 1:1 value mapping.
// deviations should have hand-written converters.
export function filterToSearchTypeMirror(
  sf: SearchFilter,
  valueModifier?: (v: any) => any,
): SearchType {
  const [kind] = Object.keys(sf);
  const [subkind] = Object.keys(sf[kind]);
  const value = sf[kind][subkind]?.value;

  let stValue: any;
  if (value === undefined) {
    stValue = NONE;
  } else {
    stValue = value;
  }

  if (valueModifier) {
    stValue = valueModifier(stValue);
  }

  if (Object.keys(sf[kind][subkind]).length > 1) {
    // dev warning: expects a value to value mapping, search types that don't conform to this should be using hand-written converters.
    console.warn(
      `mirror conversion from filter:${JSON.stringify(
        sf,
      )} to SearchType expects a single value field`,
    );
  }

  return {
    kind,
    subkind,
    value: stValue,
  } as SearchType;
}

// convert search type to filter based on naming convention with 1:1 value mapping.
// deviations should have hand-written converters.
export function searchTypeToFilterMirror(
  st: SearchType,
  valueModifier?: (v: any) => any,
): SearchFilter {
  const empty: api.NoneValue = {};
  const sf: SearchFilter = {
    [st.kind]: {
      [st.subkind]: empty, // Generated types treat "none" as {} type:NoneValue.
    },
  };
  // TODO: we may want to treat null as valid value.
  if (st.value != null && st.value != NONE) {
    let value = st.value;
    if (valueModifier) {
      value = valueModifier(value);
    }
    sf[st.kind][st.subkind] = {
      value,
    };
  }
  return sf;
}

export function filterToSearchTypeDateRangeValue(sf: SearchFilter): SearchType {
  return filterToSearchTypeMirror(sf, protoTimeRangeToDates);
}

export function fromDateRangeToProtoRange(v: [Date, Date]): api.TimeRange {
  // unconditionally bump the time range, since we use {] (RNGCO) as the
  // search operator
  const endTime = new Date(v[1]);
  endTime.setMilliseconds(endTime.getMilliseconds() + 1);
  const _v = fromDateRange([v[0], endTime]);
  const timeRange: api.TimeRange = {
    startTime: _v[0],
    endTime: _v[1],
  };
  return timeRange;
}

export function fromStringProtoToNumeric(x: string): number {
  if (x === null) return null;
  const parsed = parseFloat(x);
  if (isNaN(parsed)) {
    return null;
  }
  return parsed;
}

export function getNumericModifyFuncs(
  varType: VarType.Int | VarType.Real | VarType.Ints | VarType.Reals,
) {
  switch (varType) {
    case VarType.Int:
    case VarType.Ints:
      return {
        searchTypeToFilterModifier: forEachValue(fromNumericToStringProto),
        filterToSearchTypeModifier: forEachValue(fromStringProtoToNumeric),
      };
    case VarType.Real:
    case VarType.Reals:
      return {
        searchTypeToFilterModifier: null,
        filterToSearchTypeModifier: null,
      };
    default:
      assertExhaustive(varType);
  }
}

export function fromNumericToStringProto(v: number | null): string | null {
  return v !== null ? v.toString() : null;
}

export function searchTypeToFilterNumericValue(st: SearchType): SearchFilter {
  return searchTypeToFilterMirror(st, fromNumericToStringProto);
}

export function filterToSearchTypeNumericValue(sf: SearchFilter): SearchType {
  return filterToSearchTypeMirror(sf, fromStringProtoToNumeric);
}

export function searchTypeToFilterNumericValues(st: SearchType): SearchFilter {
  return searchTypeToFilterMirror(
    st,
    forEachValue(v => fromNumericToStringProto(v)),
  );
}

export function filterToSearchTypeNumericValues(sf: SearchFilter): SearchType {
  return filterToSearchTypeMirror(
    sf,
    forEachValue(v => fromStringProtoToNumeric(v)),
  );
}

export function searchTypeToFilterFloatRangeValue(st: SearchType): SearchFilter {
  return searchTypeToFilterMirror(
    st,
    (v: [number, number]): api.FloatRange => ({ start: v[0], end: v[1] }),
  );
}

export function filterToSearchTypeFloatRangeValue(sf: SearchFilter): SearchType {
  return filterToSearchTypeMirror(sf, (v: api.FloatRange): [number, number] => [v.start, v.end]);
}

export function searchTypeToFilterNumericRangeValue(st: SearchType): SearchFilter {
  return searchTypeToFilterMirror(
    st,
    (v: [number, number]): api.NumericRange => ({
      start: int64.fromNumber(v[0]),
      end: int64.fromNumber(v[1]),
    }),
  );
}

export function filterToSearchTypeNumericRangeValue(sf: SearchFilter): SearchType {
  return filterToSearchTypeMirror(sf, (v: api.NumericRange): [number, number] => [
    int64.toNumber(v.start),
    int64.toNumber(v.end),
  ]);
}

export function searchTypeToFilterNamedFloatRangeValue(
  st: Extract<DependentType, { field: CustomFieldIdent }>,
): SearchFilter {
  return searchTypeToFilterWithField(
    st,
    (v: [number, number]): api.FloatRange => ({ start: v[0], end: v[1] }),
  );
}

export function filterToSearchTypeNamedFloatRangeValue(
  sf: SearchFilter,
): Extract<DependentType, { field: CustomFieldIdent }> {
  return filterToSearchTypeWithField(sf, (v: api.FloatRange): [number, number] => [v.start, v.end]);
}

export function searchTypeToFilterNamedNumericRangeValue(
  st: Extract<DependentType, { name: string }>,
): SearchFilter {
  return searchTypeToFilterWithField(
    st,
    (v: [number, number]): api.NumericRange => ({
      start: int64.fromNumber(v[0]),
      end: int64.fromNumber(v[1]),
    }),
  );
}

export function filterToSearchTypeWithFieldNumericRangeValue(
  sf: SearchFilter,
): Extract<DependentType, { field: CustomFieldIdent }> {
  return filterToSearchTypeWithField(sf, (v: api.NumericRange): [number, number] => [
    int64.toNumber(v.start),
    int64.toNumber(v.end),
  ]);
}

export function searchTypeToFilterDateRangeValue(st: SearchType): SearchFilter {
  return searchTypeToFilterMirror(st, fromDateRangeToProtoRange);
}

export function filterToSearchTypeDateValue(sf: SearchFilter): SearchType {
  return filterToSearchTypeMirror(sf, toDate);
}

export function searchTypeToFilterDateValue(st: SearchType): SearchFilter {
  return searchTypeToFilterMirror(st, fromDate);
}

export function filterToSearchTypeWithFieldDateRangeValue(
  sf: SearchFilter,
): Extract<DependentType, { field: CustomFieldIdent }> {
  return filterToSearchTypeWithField(sf, protoTimeRangeToDates);
}

export function searchTypeToFilterWithFieldDateRangeValue(
  st: Extract<DependentType, { field: CustomFieldIdent }>,
): SearchFilter {
  return searchTypeToFilterWithField(st, fromDateRangeToProtoRange);
}

export function filterToSearchTypeNamedDateValue(
  sf: SearchFilter,
): Extract<DependentType, { field: CustomFieldIdent }> {
  return filterToSearchTypeWithField(sf, toDate);
}

export function searchTypeToFilterWithFieldDateValue(
  st: Extract<DependentType, { field: CustomFieldIdent }>,
): SearchFilter {
  return searchTypeToFilterWithField(st, fromDate);
}

/**
 * Returns a search filter that represents the given date as a range from the start of that date to the end of that day
 * @param st A search type
 */
export function searchTypeToFilterWithFieldFromSingleDateAsRange(
  st: Extract<SearchType, { field: CustomFieldIdent; value: Date | null }>,
): SearchFilter {
  const range = fromSingleDateAsRange(st.value);
  switch (st.field.type) {
    case CustomFieldType.Name:
      return {
        [st.kind]: {
          range: {
            name: st.field.name,
            value: {
              startTime: range[0],
              endTime: range[1],
            },
          },
        },
      };
    case CustomFieldType.Spec:
      return {
        [st.kind]: {
          range: {
            spec: st.field.slug,
            value: {
              startTime: range[0],
              endTime: range[1],
            },
          },
        },
      };
    default:
      assertExhaustive(st.field);
  }
}

// For SearchTypes of the form { value, field }. Where the kind <-> filter key and subkind <-> filter subkey match.
export function filterToSearchTypeWithField(
  sf: SearchFilter,
  valueModifier?: (v: any) => any,
): Extract<DependentType, { field: CustomFieldIdent }> {
  const [rootKey] = Object.keys(sf);
  const [childKey] = Object.keys(sf[rootKey]);
  const name = sf[rootKey][childKey]['name'];
  const spec = sf[rootKey][childKey]['spec'];
  const rawValue = sf[rootKey][childKey]['value'];
  if ((name === undefined && spec === undefined) || rawValue === undefined) {
    throw `filter path expects to have a name/spec and value`;
  }
  const value = valueModifier ? valueModifier(rawValue) : rawValue;
  const field: CustomFieldIdent =
    name !== undefined
      ? { type: CustomFieldType.Name, name }
      : { type: CustomFieldType.Spec, slug: spec };
  // We cast because the kind/subkind are stringly typed when extracted this way.
  return {
    kind: rootKey,
    subkind: childKey,
    value,
    field,
  } as Extract<DependentType, { field: CustomFieldIdent }>;
}

export function filterToSearchTypeWithFieldIsSet(
  sf: SearchFilter,
): Extract<DependentType, { field: CustomFieldIdent }> {
  const [rootKey] = Object.keys(sf);
  const [childKey] = Object.keys(sf[rootKey]);
  const name = sf[rootKey][childKey]['name'];
  const spec = sf[rootKey][childKey]['spec'];
  if (name === undefined && spec === undefined) {
    throw `filter path expects to have a name/spec`;
  }
  const field: CustomFieldIdent =
    name !== undefined
      ? { type: CustomFieldType.Name, name }
      : { type: CustomFieldType.Spec, slug: spec };
  return {
    kind: rootKey,
    subkind: childKey,
    value: NONE,
    field,
  } as Extract<DependentType, { field: CustomFieldIdent }>;
}

export function apiErrorSearchTypeToFilter(st: SearchType) {
  return {
    [st.subkind]: {
      [st.value as string]: {},
    },
  };
}

/**
 * This function converts the incoming search filter to a search type by interpreting the
 * range.
 *   1. If the Search Filter to interpret is a positive search filter and an explicit range,
 *      then it attempts to check if the range is exactly 1 day in length. If so, this is a
 *      UiOnlySubkind and not a range (i.e. is exactly this day). This is only the case if the
 *      filter to interpret as is a positive. We don't have a concept of not this day.
 *   2. The last/notLast operators are always solr durations and will be parsed as such.
 *   3. All others are parsed as regular range/notRange types.
 * @param sf A search filter
 * @param not Whether the search filter is positive or negative
 */
export function filterToSearchTypeNameDateRange(sf: SearchFilter, not = false): SearchType {
  const [rootKey] = Object.keys(sf);
  const [childKey] = Object.keys(sf[rootKey]);
  if (sf[rootKey][childKey]['name'] === undefined || sf[rootKey][childKey]['value'] === undefined) {
    throw `filter path expects to have a name and value`;
  }
  const value: api.TimeRange = sf[rootKey][childKey]['value'];
  const name: string = sf[rootKey][childKey]['name'];
  const spec: string = sf[rootKey][childKey]['spec'];
  const field: CustomFieldIdent =
    name !== undefined
      ? { type: CustomFieldType.Name, name }
      : { type: CustomFieldType.Spec, slug: spec };
  switch (true) {
    case !not && toSingleDateFromRange([value.startTime, value.endTime]) != null:
      return {
        kind: rootKey,
        subkind: UiOnlySubkinds.isDateLike,
        value: toSingleDateFromRange([value.startTime, value.endTime]),
        field,
      } as SearchType;
    case toDurationFromSolr([value.startTime, value.endTime]) != null:
      let isFuture = false; // for inTheNext operator
      if (parseSolrTime(value.startTime).scalar == 0) {
        isFuture = true;
      }
      return {
        kind: rootKey,
        subkind: isFuture
          ? not
            ? UiOnlySubkinds.notNextDurationLike
            : UiOnlySubkinds.nextDurationLike
          : not
          ? UiOnlySubkinds.notLastDurationLike
          : UiOnlySubkinds.lastDurationLike,
        value: toDurationFromSolr([value.startTime, value.endTime]),
        field,
      } as SearchType;
    default:
      return filterToSearchTypeWithFieldDateRangeValue(sf);
  }
}

export function searchTypeToFilterWithFieldDurationRange(
  st: Extract<SearchType, { field: CustomFieldIdent; value: Duration }>,
  not,
  isFuture = false, // whether duration should additive for "in the next" operator
): SearchFilter {
  const solrRelativeTime = fromDurationToSolr(st.value, isFuture);
  const range: api.TimeRange = {
    startTime: solrRelativeTime[0],
    endTime: solrRelativeTime[1],
  };
  switch (st.field.type) {
    case CustomFieldType.Name:
      return {
        [st.kind]: {
          [not ? 'notRange' : 'range']: {
            name: st.field.name,
            value: range,
          },
        },
      };
    case CustomFieldType.Spec:
      return {
        [st.kind]: {
          [not ? 'notRange' : 'range']: {
            spec: st.field.slug,
            value: range,
          },
        },
      };
    default:
      assertExhaustive(st.field);
  }
}

// For SearchTypes of the form { value, name }. Where the kind <-> filter key and subkind <-> filter subkey match.
export function searchTypeToFilterWithField(
  st: Extract<DependentType, { field: CustomFieldIdent }>,
  valueModifier?: (v: any) => any,
): SearchFilter {
  const value = valueModifier ? valueModifier(st.value) : st.value;
  switch (st.field.type) {
    case CustomFieldType.Name:
      return {
        [st.kind]: {
          [st.subkind]: {
            value,
            name: st.field.name,
          },
        },
      };
    case CustomFieldType.Spec:
      return {
        [st.kind]: {
          [st.subkind]: {
            value,
            spec: st.field.slug,
          },
        },
      };
    default:
      assertExhaustive(st.field);
  }
}

export function searchTypeToFilterWithFieldIsSet(
  st: Extract<DependentType, { field: CustomFieldIdent }>,
): SearchFilter {
  switch (st.field.type) {
    case CustomFieldType.Name:
      return {
        [st.kind]: {
          [st.subkind]: {
            name: st.field.name,
          },
        },
      };
    case CustomFieldType.Spec:
      return {
        [st.kind]: {
          [st.subkind]: {
            spec: st.field.slug,
          },
        },
      };
    default:
      assertExhaustive(st.field);
  }
}

export function filterToSearchTypeEventVisitedUrl(
  sf: SearchFilter,
): Extract<EventType, { kind: segment.EventFilter.filter_keys.visitedUrl }> {
  return filterToSearchTypeUrl(sf) as Extract<
    EventType,
    { kind: segment.EventFilter.filter_keys.visitedUrl }
  >;
}

export function filterToSearchTypeEventPageRefresh(
  sf: SearchFilter,
): Extract<EventType, { kind: segment.EventFilter.filter_keys.pageRefresh }> {
  return filterToSearchTypeUrl(sf) as Extract<
    EventType,
    { kind: segment.EventFilter.filter_keys.pageRefresh }
  >;
}

export function filterToSearchTypeAjaxVisitedUrl(
  sf: SearchFilter,
): Extract<EventType, { kind: segment.EventFilter.filter_keys.ajaxErrorUrl }> {
  return filterToSearchTypeUrl(sf) as Extract<
    EventType,
    { kind: segment.EventFilter.filter_keys.ajaxErrorUrl }
  >;
}

export function filterToSearchTypeDependentUrl(
  sf: SearchFilter,
): Extract<DependentType, { kind: segment.DependentEventFilter.filter_keys.url }> {
  return filterToSearchTypeUrl(sf) as Extract<
    DependentType,
    { kind: segment.DependentEventFilter.filter_keys.url }
  >;
}

export function filterToSearchTypeReferrerUrl(
  sf: SearchFilter,
): Extract<DependentType, { kind: segment.DependentEventFilter.filter_keys.referrerUrl }> {
  return filterToSearchTypeUrl(sf) as Extract<
    DependentType,
    { kind: segment.DependentEventFilter.filter_keys.referrerUrl }
  >;
}

export function filterToSearchTypeWebSourceFileUrl(
  sf: SearchFilter,
): Extract<DependentType, { kind: segment.DependentEventFilter.filter_keys.webSourceFileUrl }> {
  return filterToSearchTypeUrl(sf) as Extract<
    DependentType,
    { kind: segment.DependentEventFilter.filter_keys.webSourceFileUrl }
  >;
}

// For SearchTypes of the form { value, name }. Where the kind <-> filter key and subkind <-> filter subkey match.
function filterToSearchTypeUrl(sf: SearchFilter):
  | Extract<
      DependentType,
      {
        kind:
          | segment.DependentEventFilter.filter_keys.url
          | segment.DependentEventFilter.filter_keys.referrerUrl
          | segment.DependentEventFilter.filter_keys.ajaxRequestUrl
          | segment.DependentEventFilter.filter_keys.webSourceFileUrl;
      }
    >
  | Extract<EventType, EventUrlKinds> {
  const [rootKey] = Object.keys(sf);
  const [childKey] = Object.keys(sf[rootKey]);

  const value = sf[rootKey][childKey].value as api.UrlValue['value'];
  if (!value || value.length == 0) {
    return null;
  }

  const stValue: UrlValue[] = value.map(v => ({
    value: v.value,
    category: protoUrlCategoryToUiUrlCategory(v.category),
  }));

  // We cast because the kind/subkind are stringly typed when extracted this way.
  return {
    kind: rootKey,
    subkind: childKey,
    value: stValue,
  } as Extract<EventType, EventUrlKinds>;
}

// For SearchTypes of the form { value, name }. Where the kind <-> filter key and subkind <-> filter subkey match.
export function searchTypeToFilterUrl<T extends SearchType>(st: T): SearchFilter {
  if (!isUrlTypeWithValue(st)) {
    return null;
  }
  const urlValue: api.UrlValue = {
    value: [],
  };
  const values: api.Url[] = st.value.map(v => ({
    category: uiUrlCategoryToProtoUrlCategory(v.category ?? null),
    value: v.value,
  }));
  urlValue.value = values;

  return {
    [st.kind]: {
      [st.subkind]: urlValue,
    },
  } as SearchFilter;
}

export function uiUrlCategoryToProtoUrlCategory(cat: UrlCategory | null): api.UrlCategory {
  switch (cat) {
    case null:
      return api.UrlCategory.URL_INVALID;
    case UrlCategory.full:
      return api.UrlCategory.URL_FULL;
    case UrlCategory.host:
      return api.UrlCategory.URL_HOST;
    case UrlCategory.new_path:
      return api.UrlCategory.URL_NEW_PATH;
    case UrlCategory.path:
      return api.UrlCategory.URL_PATH;
    case UrlCategory.query:
      return api.UrlCategory.URL_QUERY;
    case UrlCategory.fragment:
      return api.UrlCategory.URL_FRAGMENT;
    default:
      assertExhaustive(cat);
  }
}

function protoUrlCategoryToUiUrlCategory(cat: api.UrlCategory): UrlCategory {
  switch (cat) {
    case api.UrlCategory.URL_FULL:
      return UrlCategory.full;
    case api.UrlCategory.URL_NEW_PATH:
      return UrlCategory.new_path;
    case api.UrlCategory.URL_HOST:
      return UrlCategory.host;
    case api.UrlCategory.URL_PATH:
      return UrlCategory.path;
    case api.UrlCategory.URL_QUERY:
      return UrlCategory.query;
    case api.UrlCategory.URL_FRAGMENT:
      return UrlCategory.fragment;
    case api.UrlCategory.URL_STANDARD_PATH:
    // not currently supported in ui
    // fallthrough
    case api.UrlCategory.URL_HASH_PATH:
    // not currently supported in ui
    // fallthrough
    case api.UrlCategory.URL_STANDARD_QUERY:
    // not currently supported in ui
    // fallthrough
    case api.UrlCategory.URL_HASH_QUERY:
    // not currently supported in ui
    // fallthrough
    case api.UrlCategory.URL_INVALID:
      return null;
    default:
      assertExhaustive(cat);
  }
}

export function forEachValue(valueModifier: (vm: any) => any) {
  return (v: unknown) => {
    if (v instanceof Array) {
      return v.map(valueModifier);
    }
    return valueModifier(v);
  };
}

export function uiDeviceToProtoDevice(device: Device): api.Device {
  switch (device) {
    case Device.any:
      return api.Device.DEVICE_INVALID;
    case Device.unknown:
      return api.Device.DEVICE_UNKNOWN;
    case Device.desktop:
      return api.Device.DEVICE_DESKTOP;
    case Device.mobile:
      return api.Device.DEVICE_MOBILE;
    case Device.tablet:
      return api.Device.DEVICE_TABLET;
    case Device.robot:
      return api.Device.DEVICE_ROBOT;
    default:
      assertExhaustive(device);
  }
}

export function protoPlatformToUiPlatform(platform: api.Platform): Platform {
  switch (platform) {
    case api.Platform.PLATFORM_INVALID:
      return Platform.any;
    case api.Platform.PLATFORM_WEB:
      return Platform.web;
    case api.Platform.PLATFORM_NATIVEMOBILE:
      return Platform.nativemobile;
    case api.Platform.PLATFORM_SERVER:
      return Platform.server;
    default:
      assertExhaustive(platform);
  }
}

export function uiPlatformToProtoPlatform(platform: Platform): api.Platform {
  switch (platform) {
    case Platform.any:
      return api.Platform.PLATFORM_INVALID;
    case Platform.web:
      return api.Platform.PLATFORM_WEB;
    case Platform.nativemobile:
      return api.Platform.PLATFORM_NATIVEMOBILE;
    case Platform.server:
      return api.Platform.PLATFORM_SERVER;
    default:
      assertExhaustive(platform);
  }
}

export function protoDeviceToUiDevice(device: api.Device): Device {
  switch (device) {
    case api.Device.DEVICE_INVALID:
      return Device.any;
    case api.Device.DEVICE_UNKNOWN:
      return Device.unknown;
    case api.Device.DEVICE_DESKTOP:
      return Device.desktop;
    case api.Device.DEVICE_MOBILE:
      return Device.mobile;
    case api.Device.DEVICE_TABLET:
      return Device.tablet;
    case api.Device.DEVICE_ROBOT:
      return Device.robot;
    default:
      assertExhaustive(device);
  }
}

export function uiBrowserToProtoBrowser(browser: Browser): api.Browser {
  switch (browser) {
    case Browser.unknown:
      return api.Browser.BROWSER_UNKNOWN;
    case Browser.any:
      return api.Browser.BROWSER_INVALID; // Not supported on server.
    case Browser.coccoc:
      return api.Browser.BROWSER_COCCOC;
    case Browser.chrome:
      return api.Browser.BROWSER_CHROME;
    case Browser.customagent:
      return api.Browser.BROWSER_CUSTOMAGENT;
    case Browser.custommobileagent:
      return api.Browser.BROWSER_CUSTOMMOBILEAGENT;
    case Browser.duckduckgo:
      return api.Browser.BROWSER_DUCKDUCKGO;
    case Browser.electron:
      return api.Browser.BROWSER_ELECTRON;
    case Browser.ie:
      return api.Browser.BROWSER_IE;
    case Browser.edge:
      return api.Browser.BROWSER_EDGE;
    case Browser.firefox:
      return api.Browser.BROWSER_FIREFOX;
    case Browser.lynx:
      return api.Browser.BROWSER_LYNX;
    case Browser.mozillaother:
      return api.Browser.BROWSER_MOZILLAOTHER;
    case Browser.safari:
      return api.Browser.BROWSER_SAFARI;
    case Browser.seamonkey:
      return api.Browser.BROWSER_SEAMONKEY;
    case Browser.opera:
      return api.Browser.BROWSER_OPERA;
    case Browser.robot:
      return api.Browser.BROWSER_ROBOT;
    case Browser.ucweb:
      return api.Browser.BROWSER_UCWEB;
    case Browser.yandex:
      return api.Browser.BROWSER_YANDEX;
    case Browser.mobileapp:
      return api.Browser.BROWSER_MOBILEAPP;
    default:
      assertExhaustive(browser);
  }
}

export function protoOsToUiOs(os: api.OS): OS {
  switch (os) {
    case api.OS.OS_INVALID:
      return OS.any;
    case api.OS.OS_UNKNOWN:
      return OS.unknown;
    case api.OS.OS_WINDOWS:
      return OS.windows;
    case api.OS.OS_OSX:
      return OS.osx;
    case api.OS.OS_CHROME:
      return OS.chrome;
    case api.OS.OS_ANDROID:
      return OS.android;
    case api.OS.OS_IOS:
      return OS.ios;
    case api.OS.OS_LINUX:
      return OS.linux;
    case api.OS.OS_WINDOWSPHONE:
      return OS.windowsphone;
    case api.OS.OS_ROBOT:
      return OS.robot;
    default:
      assertExhaustive(os);
  }
}

export function uiOsToProtoOs(os: OS): api.OS {
  switch (os) {
    case OS.any:
      return api.OS.OS_INVALID;
    case OS.unknown:
      return api.OS.OS_UNKNOWN;
    case OS.windows:
      return api.OS.OS_WINDOWS;
    case OS.osx:
      return api.OS.OS_OSX;
    case OS.chrome:
      return api.OS.OS_CHROME;
    case OS.android:
      return api.OS.OS_ANDROID;
    case OS.ios:
      return api.OS.OS_IOS;
    case OS.linux:
      return api.OS.OS_LINUX;
    case OS.windowsphone:
      return api.OS.OS_WINDOWSPHONE;
    case OS.robot:
      return api.OS.OS_ROBOT;
    default:
      if ((os as string) == 'Chrome') {
        return api.OS.OS_CHROME;
      }
      assertExhaustive(os);
  }
}

export function protoBrowserToUiBrowser(browser: api.Browser): Browser {
  switch (browser) {
    case api.Browser.BROWSER_UNKNOWN:
      return Browser.unknown;
    case api.Browser.BROWSER_INVALID:
      return Browser.any; // Fallback to any.
    case api.Browser.BROWSER_COCCOC:
      return Browser.coccoc;
    case api.Browser.BROWSER_CHROME:
      return Browser.chrome;
    case api.Browser.BROWSER_CUSTOMAGENT:
      return Browser.customagent;
    case api.Browser.BROWSER_CUSTOMMOBILEAGENT:
      return Browser.custommobileagent;
    case api.Browser.BROWSER_DUCKDUCKGO:
      return Browser.duckduckgo;
    case api.Browser.BROWSER_ELECTRON:
      return Browser.electron;
    case api.Browser.BROWSER_IE:
      return Browser.ie;
    case api.Browser.BROWSER_EDGE:
      return Browser.edge;
    case api.Browser.BROWSER_FIREFOX:
      return Browser.firefox;
    case api.Browser.BROWSER_LYNX:
      return Browser.lynx;
    case api.Browser.BROWSER_MOZILLAOTHER:
      return Browser.mozillaother;
    case api.Browser.BROWSER_SAFARI:
      return Browser.safari;
    case api.Browser.BROWSER_SEAMONKEY:
      return Browser.seamonkey;
    case api.Browser.BROWSER_OPERA:
      return Browser.opera;
    case api.Browser.BROWSER_ROBOT:
      return Browser.robot;
    case api.Browser.BROWSER_UCWEB:
      return Browser.ucweb;
    case api.Browser.BROWSER_YANDEX:
      return Browser.yandex;
    case api.Browser.BROWSER_MOBILEAPP:
      return Browser.mobileapp;
    default:
      assertExhaustive(browser);
  }
}

export function protoPinchGestureToUiPinchGesture(gesture: api.PinchGesture): PinchGesture {
  switch (gesture) {
    case api.PinchGesture.PINCH_GESTURE_SCALE_IN:
      return PinchGesture.in;
    case api.PinchGesture.PINCH_GESTURE_SCALE_OUT:
      return PinchGesture.out;
    case api.PinchGesture.PINCH_GESTURE_INVALID:
      return PinchGesture.in; // fallback to in
    default:
      return assertExhaustive(gesture);
  }
}

export function uiPinchGestureToProtoPinchGesture(gesture: PinchGesture): api.PinchGesture {
  switch (gesture) {
    case PinchGesture.in:
      return api.PinchGesture.PINCH_GESTURE_SCALE_IN;
    case PinchGesture.out:
      return api.PinchGesture.PINCH_GESTURE_SCALE_OUT;
    default:
      return assertExhaustive(gesture);
  }
}

export function protoSeenVisibilityToUiSeenVisibility(
  visibility: api.SeenVisibility,
): SeenVisibility {
  switch (visibility) {
    case api.SeenVisibility.SEEN_VISIBILITY_IN_VIEWPORT:
      return SeenVisibility.InViewport;
    case api.SeenVisibility.SEEN_VISIBILITY_NOT_IN_VIEWPORT:
      return SeenVisibility.NotInViewport;
    case api.SeenVisibility.SEEN_VISIBILITY_UNKNOWN:
      return SeenVisibility.InViewport; // fallback to in
    default:
      return assertExhaustive(visibility);
  }
}

export function uiSeenVisibilityToProtoSeenVisibility(
  visibility: SeenVisibility,
): api.SeenVisibility {
  switch (visibility) {
    case SeenVisibility.InViewport:
      return api.SeenVisibility.SEEN_VISIBILITY_IN_VIEWPORT;
    case SeenVisibility.NotInViewport:
      return api.SeenVisibility.SEEN_VISIBILITY_NOT_IN_VIEWPORT;
    default:
      return assertExhaustive(visibility);
  }
}

export function protoErrorClickTypeToUiErrorClickType(
  errorClickType: api.ErrorClickType,
): ErrorClickType {
  switch (errorClickType) {
    case api.ErrorClickType.ERROR_CLICK_TYPE_INVALID:
      return ErrorClickType.any;
    case api.ErrorClickType.ERROR_CLICK_TYPE_UNKNOWN:
      return ErrorClickType.any; // fallback to any
    case api.ErrorClickType.ERROR_CLICK_TYPE_CONSOLE:
      return ErrorClickType.console;
    case api.ErrorClickType.ERROR_CLICK_TYPE_EXCEPTION:
      return ErrorClickType.exception;
    default:
      assertExhaustive(errorClickType);
  }
}

export function uiErrorClickTypeToProtoErrorClickType(
  errorClickType: ErrorClickType,
): api.ErrorClickType {
  switch (errorClickType) {
    case ErrorClickType.any:
      return api.ErrorClickType.ERROR_CLICK_TYPE_INVALID;
    case ErrorClickType.console:
      return api.ErrorClickType.ERROR_CLICK_TYPE_CONSOLE;
    case ErrorClickType.exception:
      return api.ErrorClickType.ERROR_CLICK_TYPE_EXCEPTION;
    default:
      assertExhaustive(errorClickType);
  }
}

export function protoDatalossTypeToUiDatalossType(datalossType: api.DatalossType): DatalossType {
  switch (datalossType) {
    case api.DatalossType.DATALOSS_TYPE_NONE:
      return DatalossType.paymentcard; // fallback to payment card
    case api.DatalossType.DATALOSS_TYPE_PAYMENT_CARD:
      return DatalossType.paymentcard;
    case api.DatalossType.DATALOSS_TYPE_EMAIL:
      return DatalossType.email;
    case api.DatalossType.DATALOSS_TYPE_SSN:
      return DatalossType.ssn;
    case api.DatalossType.DATALOSS_TYPE_PASSWORD:
      return DatalossType.password;
    default:
      assertExhaustive(datalossType);
  }
}

export function uiDatalossTypeToProtoDatalossType(datalossType: DatalossType): api.DatalossType {
  switch (datalossType) {
    case DatalossType.paymentcard:
      return api.DatalossType.DATALOSS_TYPE_PAYMENT_CARD;
    case DatalossType.email:
      return api.DatalossType.DATALOSS_TYPE_EMAIL;
    case DatalossType.ssn:
      return api.DatalossType.DATALOSS_TYPE_SSN;
    case DatalossType.password:
      return api.DatalossType.DATALOSS_TYPE_PASSWORD;
    default:
      assertExhaustive(datalossType);
  }
}

export function protoSessionTypeToUiSessionType(sessionType: api.SessionType): SessionType {
  switch (sessionType) {
    case api.SessionType.SESSION_TYPE_INVALID:
      return SessionType.any;
    case api.SessionType.SESSION_TYPE_LIVE:
      return SessionType.live;
    case api.SessionType.SESSION_TYPE_PREVIEW:
      return SessionType.preview;
    default:
      assertExhaustive(sessionType);
  }
}

export function uiSessionTypeToProtoSessionType(sessionType: SessionType): api.SessionType {
  switch (sessionType) {
    case SessionType.any:
      return api.SessionType.SESSION_TYPE_INVALID;
    case SessionType.live:
      return api.SessionType.SESSION_TYPE_LIVE;
    case SessionType.preview:
      return api.SessionType.SESSION_TYPE_PREVIEW;
    default:
      assertExhaustive(sessionType);
  }
}

export function toProtoDuration(dur: Duration): protobuf.duration {
  return protobuf.Duration.fromMillis(durationScalarMs.get(dur.unit) * dur.scalar);
}

export function toProtoDurationRange(durs: [Duration, Duration]): api.DurationRangeValue['value'] {
  return {
    start: toProtoDuration(durs[0]),
    end: toProtoDuration(durs[1]),
  };
}

export function fromProtoDuration(
  pd: api.DurationValue['value'],
  minUnit?: DurationUnit,
): Duration {
  return toDurationFromMilli(protobuf.Duration.toMillis(pd), minUnit);
}

export function fromProtoDurationRange(
  pd: api.DurationRangeValue['value'],
  minUnit?: DurationUnit,
): [Duration, Duration] {
  return [
    toDurationFromMilli(protobuf.Duration.toMillis(pd.start), minUnit),
    toDurationFromMilli(protobuf.Duration.toMillis(pd.end), minUnit),
  ];
}

export function newConverterMap<T extends SearchType, K extends Kind<T>>(): SubkindConverterMap<
  T,
  K
> {
  return {} as SubkindConverterMap<T, K>;
}

export function numericConverter<T extends SearchType, K extends Kind<T>>(
  kind: K,
  field: string,
  extractIdx: number,
  ...adjustFuncs: adjuster<T>[]
): SubkindConverterMap<T, K> {
  const result = newConverterMap<T, K>();
  const operators: Map<string, SearchOperator> = new Map([
    [segment.NumericOperator.operator_keys.equals, Op.SEARCH_OP_EQ],
    [segment.NumericOperator.operator_keys.notEquals, Op.SEARCH_OP_NEQ],
    [segment.NumericOperator.operator_keys.lessThan, Op.SEARCH_OP_LT],
    [segment.NumericOperator.operator_keys.greaterThan, Op.SEARCH_OP_GT],
    [segment.NumericOperator.operator_keys.atMost, Op.SEARCH_OP_LTE],
    [segment.NumericOperator.operator_keys.atLeast, Op.SEARCH_OP_GTE],
  ]);

  for (const [protoOperator, searchOperator] of operators.entries()) {
    // Only a couple of these are repeated numerics, the rest need to be unwrapped
    result[protoOperator] = {
      criteria: s => [{ Field: field, Op: searchOperator, Value: s.value }],
      withoutPartials: s => stripPartials(s, notNullPartial),
      extractFromCriteria: (c: DocumentCriterion[]) =>
        c[extractIdx] && {
          kind,
          subkind: protoOperator,
          value: c[extractIdx].Value as number[],
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == field &&
        c[0].Op == searchOperator && {
          kind,
          subkind: protoOperator,
          value: [null],
        },
      searchTypeToFilter: searchTypeToFilterNumericValues,
      filterToSearchType: filterToSearchTypeNumericValues,
    };
  }
  for (const rangeOperator of [
    segment.NumericOperator.operator_keys.range,
    segment.NumericOperator.operator_keys.notRange,
  ]) {
    result[rangeOperator] = {
      withoutPartials: s => stripPartials(s, multiNotNull),
      searchTypeToFilter: searchTypeToFilterNumericRangeValue,
      filterToSearchType: filterToSearchTypeNumericRangeValue,
    };
  }

  adjust(result, ...adjustFuncs);
  [
    segment.NumericOperator.operator_keys.equals,
    segment.NumericOperator.operator_keys.notEquals,
  ].forEach(value => {
    result[value].withoutPartials = s => stripPartials(s, multiNotNullPartial);
    result[value] = withValueWrapping(result[value], extractIdx);
  });
  return result;
}

export function floatOperatorConverter<T extends SearchType, K extends Kind<T>>(
  kind: K,
  field: string,
): SubkindConverterMap<T, K> {
  const result = newConverterMap<T, K>();
  const operators: Map<string, SearchOperator> = new Map([
    [segment.FloatOperator.operator_keys.equals, Op.SEARCH_OP_EQ],
    [segment.FloatOperator.operator_keys.notEquals, Op.SEARCH_OP_NEQ],
    [segment.FloatOperator.operator_keys.lessThan, Op.SEARCH_OP_LT],
    [segment.FloatOperator.operator_keys.greaterThan, Op.SEARCH_OP_GT],
    [segment.FloatOperator.operator_keys.atMost, Op.SEARCH_OP_LTE],
    [segment.FloatOperator.operator_keys.atLeast, Op.SEARCH_OP_GTE],
  ]);
  for (const [protoOperator] of operators.entries()) {
    result[protoOperator] = {
      withoutPartials: s => stripPartials(s, notNullPartial),
      searchTypeToFilter: searchTypeToFilterNumericValues,
      filterToSearchType: filterToSearchTypeNumericValues,
    };
  }
  for (const rangeOperator of [
    segment.FloatOperator.operator_keys.range,
    segment.FloatOperator.operator_keys.notRange,
  ]) {
    result[rangeOperator] = {
      withoutPartials: s => stripPartials(s, multiNotNull),
      searchTypeToFilter: searchTypeToFilterFloatRangeValue,
      filterToSearchType: filterToSearchTypeFloatRangeValue,
    };
  }
  return result;
}

/** @deprecated */
export function legacyNumericIdConverter<T extends SearchType, K extends Kind<T>>(
  kind: K,
  field: string,
  extractIdx: number,
  ...adjustFuncs: adjuster<T>[]
): SubkindConverterMap<T, K> {
  const result = newConverterMap<T, K>();
  const operators: Map<string, SearchOperator> = new Map([
    [segment.NumericIdOperator.operator_keys.equals, Op.SEARCH_OP_EQ],
    [segment.NumericIdOperator.operator_keys.notEquals, Op.SEARCH_OP_NEQ],
  ]);

  for (const [protoOperator, searchOperator] of operators.entries()) {
    // Only a couple of these are repeated numerics, the rest need to be unwrapped
    result[protoOperator] = {
      criteria: s => [{ Field: field, Op: searchOperator, Value: s.value }],
      withoutPartials: s => stripPartials(s, multiNotNullPartial),
      extractFromCriteria: (c: DocumentCriterion[]) =>
        c[extractIdx] && {
          kind,
          subkind: protoOperator,
          value: c[extractIdx].Value as number[],
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == field &&
        c[0].Op == searchOperator && {
          kind,
          subkind: protoOperator,
          value: [null],
        },
      searchTypeToFilter: searchTypeToFilterNumericValues,
      filterToSearchType: filterToSearchTypeNumericValues,
    };
  }

  result[segment.NumericIdOperator.operator_keys.any] = {
    criteria: () => null, // numeric id any not supported in legacy search model
    extractFromCriteria: () => null, // numeric id any not supported in legacy search model
    withoutPartials: s => stripPartials(s, identity),
    searchTypeToFilter: st => searchTypeToFilterMirror(st),
    filterToSearchType: sf => filterToSearchTypeMirror(sf),
  };

  adjust(result, ...adjustFuncs);
  [
    segment.NumericIdOperator.operator_keys.equals,
    segment.NumericIdOperator.operator_keys.notEquals,
  ].forEach(value => {
    result[value] = withValueWrapping(result[value], extractIdx);
  });

  return result;
}

export function numericIdConverter<T extends SearchType, K extends Kind<T>>(): SubkindConverterMap<
  T,
  K
> {
  const result = newConverterMap<T, K>();
  const operators: string[] = [
    segment.NumericIdOperator.operator_keys.equals,
    segment.NumericIdOperator.operator_keys.notEquals,
  ];

  result[segment.NumericIdOperator.operator_keys.any] = {
    withoutPartials: s => stripPartials(s, identity),
    searchTypeToFilter: st => searchTypeToFilterMirror(st),
    filterToSearchType: sf => filterToSearchTypeMirror(sf),
  };

  for (const protoOperator of operators) {
    // Only a couple of these are repeated numerics, the rest need to be unwrapped
    result[protoOperator] = {
      withoutPartials: s => stripPartials(s, multiNotNullPartial),
      searchTypeToFilter: searchTypeToFilterNumericValues,
      filterToSearchType: filterToSearchTypeNumericValues,
    };
  }

  return result;
}

export function pageIdConverter<T extends SearchType, K extends Kind<T>>(): SubkindConverterMap<
  T,
  K
> {
  const result = newConverterMap<T, K>();
  const operators: string[] = [
    segment.PageRefOperator.operator_keys.equals,
    segment.PageRefOperator.operator_keys.notEquals,
  ];

  result[segment.PageRefOperator.operator_keys.any] = {
    withoutPartials: s => stripPartials(s, identity),
    searchTypeToFilter: st => searchTypeToFilterMirror(st),
    filterToSearchType: sf => filterToSearchTypeMirror(sf),
  };

  for (const protoOperator of operators) {
    // Only a couple of these are repeated numerics, the rest need to be unwrapped
    result[protoOperator] = {
      withoutPartials: s => stripPartials(s, multiNotNullPartial),
      searchTypeToFilter: st => {
        const ids = st.value
          .filter(v => v?.type === PageRefValueType.Id)
          .map(v => fromNumericToStringProto(v.value));
        const specs = st.value.filter(v => v?.type === PageRefValueType.Spec).map(v => v.value);

        return {
          [st.kind]: {
            [protoOperator]: {
              value: ids,
              spec: specs,
            },
          },
        };
      },
      filterToSearchType: sf => {
        const [kind] = Object.keys(sf);
        const sfValue = sf[kind][protoOperator];

        const ids = sfValue?.value
          ? sfValue?.value?.map(v => ({
              type: PageRefValueType.Id,
              value: fromStringProtoToNumeric(v),
            }))
          : [];

        const specs = sfValue?.spec
          ? sfValue?.spec?.map(v => ({
              type: PageRefValueType.Spec,
              value: v,
            }))
          : [];

        return {
          kind,
          subkind: protoOperator,
          value: [...ids, ...specs] || NONE,
        };
      },
    };
  }

  return result;
}

export function boolConverter<T extends SearchType, K extends Kind<T>>(): SubkindConverterMap<
  T,
  K
> {
  const result = newConverterMap<T, K>();
  const operators: string[] = [
    segment.BoolOperator.operator_keys.equals,
    segment.BoolOperator.operator_keys.notEquals,
  ];

  for (const protoOperator of operators) {
    // Only a couple of these are repeated numerics, the rest need to be unwrapped
    result[protoOperator] = {
      withoutPartials: s => stripPartials(s, notNullPartial),
      searchTypeToFilter: st => searchTypeToFilterMirror(st),
      filterToSearchType: sf => filterToSearchTypeMirror(sf),
    };
  }

  result[segment.NumericIdOperator.operator_keys.any] = {
    withoutPartials: s => stripPartials(s, identity),
    searchTypeToFilter: st => searchTypeToFilterMirror(st),
    filterToSearchType: sf => filterToSearchTypeMirror(sf),
  };

  return result;
}

export function stringRangeConverter<T extends SearchType, K extends Kind<T>>(
  kind: K,
  field: string,
  extractIdx: number,
  ...adjustFuncs: adjuster<T>[]
): SubkindConverterMap<T, K> {
  const result = newConverterMap<T, K>();
  const operators: Map<string, SearchOperator> = new Map([
    [segment.StringRangeOperator.operator_keys.equals, Op.SEARCH_OP_EQ],
    [segment.StringRangeOperator.operator_keys.notEquals, Op.SEARCH_OP_NEQ],
    [segment.StringRangeOperator.operator_keys.lessThan, Op.SEARCH_OP_LT],
    [segment.StringRangeOperator.operator_keys.greaterThan, Op.SEARCH_OP_GT],
    [segment.StringRangeOperator.operator_keys.atMost, Op.SEARCH_OP_LTE],
    [segment.StringRangeOperator.operator_keys.atLeast, Op.SEARCH_OP_GTE],
    [segment.StringRangeOperator.operator_keys.startsWith, Op.SEARCH_OP_SW],
    [segment.StringRangeOperator.operator_keys.endsWith, Op.SEARCH_OP_EW],
  ]);

  for (const [protoOperator, searchOperator] of operators.entries()) {
    result[protoOperator] = {
      criteria: s => [{ Field: field, Op: searchOperator, Value: s.value }],
      withoutPartials: s => stripPartials(s, notNullPartial),
      extractFromCriteria: (c: DocumentCriterion[]) =>
        c[extractIdx] && {
          kind,
          subkind: protoOperator,
          value: c[extractIdx].Value as string[],
        },
      extractFromPartialCriteria: (c: DocumentCriterion[]) => {
        const toRet = {
          kind,
          subkind: protoOperator,
          value: [null],
        };

        if (partiallyEqualCriteria(result[protoOperator].criteria(toRet), c)) {
          return toRet;
        }
        return null;
      },
      searchTypeToFilter: st => searchTypeToFilterMirror(st),
      filterToSearchType: sf => filterToSearchTypeMirror(sf),
    };
  }

  adjust(result, ...adjustFuncs);
  [
    segment.StringRangeOperator.operator_keys.equals,
    segment.StringRangeOperator.operator_keys.notEquals,
    segment.StringRangeOperator.operator_keys.startsWith,
    segment.StringRangeOperator.operator_keys.endsWith,
  ].forEach(value => {
    result[value].withoutPartials = s => stripPartials(s, multiNotNullPartial);
    result[value] = withValueWrapping(result[value], extractIdx);
  });
  return result;
}

export function urlConverter<T extends SearchType, K extends Kind<T>>(
  kind: K,
  field: string,
  extractIdx: number,
  searchTypeAssertion: (s: any) => boolean,
  fieldfromCategory: Map<UrlCategory, string>,
  fieldToCategory: Map<string, UrlCategory>,
  searchTypeToFilter: (st: T) => SearchFilter,
  filterToSearchType: (sf: SearchFilter) => SearchType,
  ...adjustFuncs: adjuster<T>[]
): SubkindConverterMap<T, K> {
  function extractUrlDocValue(s: any) {
    if (!searchTypeAssertion(s)) {
      return null;
    }
    return s.value.map(v => v.value);
  }

  function extractValueWithCategoryFromDoc(c: DocumentCriterion[]): UrlValue[] {
    if (!c[extractIdx]) {
      return null;
    }

    const Value = c[extractIdx].Value;
    if (!(Value instanceof Array)) {
      return null;
    }

    return Value.map(_v => ({
      category: fieldToCategory.get(c[extractIdx].Field),
      value: _v as string,
    }));
  }

  function partialUrlCategory(category: UrlCategory | null): UrlValue[] {
    return [
      {
        category,
        value: null,
      },
    ];
  }

  function withoutEmptyValues(_s: any): { ok: boolean; s: any } {
    if (!searchTypeAssertion(_s)) {
      return {
        ok: false,
        s: {
          ..._s,
        },
      };
    }
    return {
      ok:
        _s.value.filter((v: UrlValue) => v.value != null && v.value != '' && v.category).length > 0,
      s: {
        ..._s,
        value: _s.value.filter((v: UrlValue) => v.value != null && v.value != '' && v.category),
      },
    };
  }

  const result = newConverterMap<T, K>();
  const operators: Map<string, [SearchOperator, SearchOperator]> = new Map([
    [segment.UrlOperator.operator_keys.equals, [Op.SEARCH_OP_IS, Op.SEARCH_OP_EQ]],
    [segment.UrlOperator.operator_keys.notEquals, [Op.SEARCH_OP_NIS_dontuse, Op.SEARCH_OP_NEQ]],
    [segment.UrlOperator.operator_keys.endsWith, [Op.SEARCH_OP_EW, Op.SEARCH_OP_EW]],
    [
      segment.UrlOperator.operator_keys.startsWith,
      [Op.SEARCH_OP_UNTOK_SW_dontuse, Op.SEARCH_OP_SW],
    ],
    [segment.UrlOperator.operator_keys.containsToken, [Op.SEARCH_OP_EQ, null]],
    [segment.UrlOperator.operator_keys.notContainsToken, [Op.SEARCH_OP_NEQ, null]],
    [segment.UrlOperator.operator_keys.containsTokenStartsWith, [Op.SEARCH_OP_SW, null]],
    [segment.UrlOperator.operator_keys.substring, [Op.SEARCH_OP_SUBSTR, Op.SEARCH_OP_SUBSTR]],
    // New operators, should not be backported to search criteria, so we put null here
    [segment.UrlOperator.operator_keys.matches, [null, null]],
    [segment.UrlOperator.operator_keys.notMatches, [null, null]],
    [segment.UrlOperator.operator_keys.isSet, [null, null]],
    [segment.UrlOperator.operator_keys.isUnset, [null, null]],
  ]);
  const getSearchOperator = (
    category: UrlCategory,
    searchOperators: [SearchOperator, SearchOperator],
  ) => {
    const tokenized = isTokenCategoryOrNotSet(category);
    // NB: Tokenized fields don't really support all operators, but if we are parsing from a criteria,
    //     we will accept the untokenized operators as they are typically "hidden" otherwise. These operators
    //     could exist for some saved entities due to previously rolling out the operators for a period of time.
    return tokenized ? searchOperators[0] : searchOperators[1];
  };

  for (const [protoOperator, searchOperators] of operators.entries()) {
    result[protoOperator] = {
      criteria: s => {
        const category = s.value[0] && s.value[0].category;
        const searchOperator = getSearchOperator(category, searchOperators);
        if (!searchOperator) {
          return null; // no support, will blow up later if hit
        }
        const field = fieldfromCategory.get(category);
        return [
          {
            Field: field,
            Op: searchOperator,
            Value: extractUrlDocValue(s),
          },
        ];
      },
      withoutPartials: s => withoutEmptyValues(s),
      extractFromCriteria: (c: DocumentCriterion[]) => {
        const crit = c[extractIdx];
        if (!crit) {
          return null;
        }
        const category = fieldToCategory.get(crit.Field);
        const searchOperator = getSearchOperator(category, searchOperators);
        if (!searchOperator) {
          return null; // no support
        }
        return {
          kind,
          subkind: protoOperator,
          value: extractValueWithCategoryFromDoc(c),
        };
      },
      extractFromPartialCriteria: (c: DocumentCriterion[]) => {
        const crit = c[extractIdx];
        if (!crit || !crit.Field.startsWith(field)) {
          return null;
        }
        const category = fieldToCategory.get(crit.Field);
        const searchOperator = getSearchOperator(category, searchOperators);
        if (!searchOperator) {
          return null; // no support
        }
        return (
          crit.Op == searchOperator && {
            kind,
            subkind: protoOperator,
            value: partialUrlCategory(category),
          }
        );
      },
      searchTypeToFilter,
      filterToSearchType,
    };
  }

  result[segment.UrlOperator.operator_keys.any] = {
    criteria: () => null,
    extractFromCriteria: () => null,
    withoutPartials: s => stripPartials(s, identity),
    searchTypeToFilter: st => searchTypeToFilterMirror(st),
    filterToSearchType: sf => filterToSearchTypeMirror(sf),
  };

  result[segment.UrlOperator.operator_keys.isSet] = {
    criteria: () => null,
    extractFromCriteria: () => null,
    extractFromPartialCriteria: () => null,
    withoutPartials: s => stripPartials(s, identity),
    searchTypeToFilter: st => searchTypeToFilterMirror(st),
    filterToSearchType: sf => filterToSearchTypeMirror(sf),
  };

  result[segment.UrlOperator.operator_keys.isUnset] = {
    criteria: () => null,
    extractFromCriteria: () => null,
    extractFromPartialCriteria: () => null,
    withoutPartials: s => stripPartials(s, identity),
    searchTypeToFilter: st => searchTypeToFilterMirror(st),
    filterToSearchType: sf => filterToSearchTypeMirror(sf),
  };

  adjust(result, ...adjustFuncs);
  for (const k of Object.keys(result)) {
    result[k] = withValueWrapping(result[k], extractIdx);
  }
  return result;
}

export function partiallyEqualCriteria(
  input: DocumentCriterion[],
  expected: DocumentCriterion[],
): boolean {
  if (input.length != expected.length) {
    return false;
  }

  return input.every(
    (_, idx) => input[idx].Field == expected[idx].Field && input[idx].Op == expected[idx].Op,
  );
}

export function durationOperatorConverter<T extends SearchType, K extends Kind<T>>(
  kind: K,
  field: string,
  extractIdx: number,
  durationUnits: DurationUnit,
  ...adjustFuncs: adjuster<T>[]
): SubkindConverterMap<T, K> {
  const result = newConverterMap<T, K>();
  const operators: Map<string, SearchOperator> = new Map([
    [segment.DurationOperator.operator_keys.atLeast, Op.SEARCH_OP_GTE],
    [segment.DurationOperator.operator_keys.atMost, Op.SEARCH_OP_LTE],
    [segment.DurationOperator.operator_keys.lessThan, Op.SEARCH_OP_LT],
    [segment.DurationOperator.operator_keys.greaterThan, Op.SEARCH_OP_GT],
  ]);

  for (const [protoOperator, searchOperator] of operators.entries()) {
    result[protoOperator] = {
      criteria: s => [
        { Field: field, Op: searchOperator, Value: fromDuration(s.value, durationUnits) },
      ],
      withoutPartials: s => stripPartials(s, durationPartial),
      extractFromCriteria: c => {
        if (c[extractIdx] == null) {
          return null;
        }
        const v = toDuration(c[extractIdx].Value, durationUnits);
        if (v == null) {
          return null;
        }
        return {
          kind,
          subkind: protoOperator,
          value: v,
        };
      },
      extractFromPartialCriteria: c => {
        const toRet = {
          kind,
          subkind: protoOperator,
          value: PartialDuration,
        };

        if (partiallyEqualCriteria(result[protoOperator].criteria(toRet), c)) {
          return toRet;
        }

        return null;
      },
      searchTypeToFilter: (st): SearchFilter => searchTypeToFilterMirror(st, toProtoDuration),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        filterToSearchTypeMirror(sf, fromProtoDuration),
    };
  }

  const rangeOperators = [
    segment.DurationOperator.operator_keys.range,
    segment.DurationOperator.operator_keys.notRange,
  ];
  for (const op of rangeOperators) {
    result[op] = {
      criteria: () => null,
      extractFromCriteria: () => null,
      extractFromPartialCriteria: () => null,
      withoutPartials: s =>
        stripPartials(s, value => ({
          ok: value[0].scalar !== null && value[1].scalar !== null,
          value,
        })),
      searchTypeToFilter: (st): SearchFilter => searchTypeToFilterMirror(st, toProtoDurationRange),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        filterToSearchTypeMirror(sf, fromProtoDurationRange),
    };
  }

  adjust(result, ...adjustFuncs);
  return result;
}

/** @deprecated */
export function legacyStringOperatorConverter<T extends SearchType, K extends Kind<T>>(
  kind: K,
  field: string,
  extractIdx: number,
  disabled: (flags: Flags) => boolean = undefined,
  tokenized = true,
  ...adjustFuncs: adjuster<T>[]
): SubkindConverterMap<T, K> {
  const result = newConverterMap<T, K>();
  const operators: Map<string, SearchOperator> = new Map([
    [segment.StringOperator.operator_keys.equals, Op.SEARCH_OP_IS],
    [segment.StringOperator.operator_keys.notEquals, Op.SEARCH_OP_NIS_dontuse],
    [segment.StringOperator.operator_keys.startsWith, Op.SEARCH_OP_UNTOK_SW_dontuse],
    [segment.StringOperator.operator_keys.endsWith, Op.SEARCH_OP_EW],
    [segment.StringOperator.operator_keys.containsToken, Op.SEARCH_OP_EQ],
    [segment.StringOperator.operator_keys.containsTokenStartsWith, Op.SEARCH_OP_SW],
    [segment.StringOperator.operator_keys.notContainsToken, Op.SEARCH_OP_NEQ],
    [segment.StringOperator.operator_keys.substring, Op.SEARCH_OP_SUBSTR],
  ]);

  const tokenizedEquivalents = new Map<string, SearchOperator>([
    [segment.StringOperator.operator_keys.equals, Op.SEARCH_OP_EQ],
    [segment.StringOperator.operator_keys.startsWith, Op.SEARCH_OP_SW],
    [segment.StringOperator.operator_keys.notEquals, SearchOperator.SEARCH_OP_NEQ],
  ]);

  const tokenizedOperators = new Set<string>([
    segment.StringOperator.operator_keys.containsToken,
    segment.StringOperator.operator_keys.containsTokenStartsWith,
    segment.StringOperator.operator_keys.notContainsToken,
  ]);

  for (let [protoOperator, searchOperator] of operators.entries()) {
    // For non tokenized operators, bump the search operators up to the equivalents
    if (!tokenized && tokenizedEquivalents.has(protoOperator)) {
      searchOperator = tokenizedEquivalents.get(protoOperator);
    }

    result[protoOperator] = {
      criteria: (s: T): DocumentCriterion[] => [
        { Field: field, Op: searchOperator, Value: s.value },
      ],
      withoutPartials: s => stripPartials(s, multiNotNullPartial),
      extractFromCriteria: (c: DocumentCriterion[]) => {
        if (!tokenized && tokenizedOperators.has(protoOperator)) {
          // Disabled
          return null;
        }
        if (c[extractIdx]) {
          return {
            kind,
            subkind: protoOperator,
            value: c[extractIdx].Value as string[],
          };
        }

        return null;
      },
      extractFromPartialCriteria: (c: DocumentCriterion[]) => {
        const toRet = {
          kind,
          subkind: protoOperator,
          value: [null],
        };

        if (partiallyEqualCriteria(result[protoOperator].criteria(toRet), c)) {
          return toRet;
        }
        return null;
      },
      searchTypeToFilter: st => searchTypeToFilterMirror(st),
      filterToSearchType: sf => filterToSearchTypeMirror(sf),
      disabled,
    };
  }
  result[segment.StringOperator.operator_keys.any] = {
    criteria: () => null,
    extractFromCriteria: () => null,
    withoutPartials: s => stripPartials(s, identity),
    searchTypeToFilter: st => searchTypeToFilterMirror(st),
    filterToSearchType: sf => filterToSearchTypeMirror(sf),
  };

  adjust<T, K>(result, ...adjustFuncs);
  for (const k of Object.keys(result)) {
    result[k] = withValueWrapping(result[k], extractIdx);
  }

  return result;
}

export function stringOperatorConverter<T extends SearchType, K extends Kind<T>>(
  disabled = false,
): SubkindConverterMap<T, K> {
  const result = newConverterMap<T, K>();
  const operators: string[] = [
    segment.StringOperator.operator_keys.equals,
    segment.StringOperator.operator_keys.notEquals,
    segment.StringOperator.operator_keys.startsWith,
    segment.StringOperator.operator_keys.endsWith,
    segment.StringOperator.operator_keys.containsToken,
    segment.StringOperator.operator_keys.containsTokenStartsWith,
    segment.StringOperator.operator_keys.notContainsToken,
    segment.StringOperator.operator_keys.substring,
  ];

  for (const protoOperator of operators) {
    result[protoOperator] = {
      withoutPartials: s => stripPartials(s, multiNotNullPartial),
      searchTypeToFilter: st => searchTypeToFilterMirror(st),
      filterToSearchType: sf => filterToSearchTypeMirror(sf),
      disabled,
    };
  }

  result[segment.StringOperator.operator_keys.any] = {
    withoutPartials: s => stripPartials(s, identity),
    searchTypeToFilter: st => searchTypeToFilterMirror(st),
    filterToSearchType: sf => filterToSearchTypeMirror(sf),
  };

  return result;
}

// elementLikeConverter provide the converter definitions for element like operators (ElementOperator, AbandonedFormOperator, ElementIdOperator).
// The constants used here are ElementOperator constants since they are the most inclusive, but the must align with the
// other element like fields.
export function elementLikeConverter<T extends SearchType, K extends Kind<T>>(
  kind: K,
  opts: {
    eventType?: string;
    modField?: string;
    includes?: segment.ElementOperator.operator_keys[];
    renames?: Map<segment.ElementOperator.operator_keys, string>;
  },
): SubkindConverterMap<T, K> {
  const result = newConverterMap<T, K>();

  const keys = new Map<segment.ElementOperator.operator_keys, string>([
    [segment.ElementOperator.operator_keys.any, segment.ElementOperator.operator_keys.any],
    [segment.ElementOperator.operator_keys.text, segment.ElementOperator.operator_keys.text],
    [
      segment.ElementOperator.operator_keys.textIsNot,
      segment.ElementOperator.operator_keys.textIsNot,
    ],
    [
      segment.ElementOperator.operator_keys.textStartsWith,
      segment.ElementOperator.operator_keys.textStartsWith,
    ],
    [
      segment.ElementOperator.operator_keys.textEndsWith,
      segment.ElementOperator.operator_keys.textEndsWith,
    ],
    [
      segment.ElementOperator.operator_keys.textContainsToken,
      segment.ElementOperator.operator_keys.textContainsToken,
    ],
    [
      segment.ElementOperator.operator_keys.textNotContainsToken,
      segment.ElementOperator.operator_keys.textNotContainsToken,
    ],
    [
      segment.ElementOperator.operator_keys.textContainsTokenStartsWith,
      segment.ElementOperator.operator_keys.textContainsTokenStartsWith,
    ],
    [segment.ElementOperator.operator_keys.css, segment.ElementOperator.operator_keys.css],
    [
      segment.ElementOperator.operator_keys.cssIsNot,
      segment.ElementOperator.operator_keys.cssIsNot,
    ],
    [
      segment.ElementOperator.operator_keys.withElementId,
      segment.ElementOperator.operator_keys.withElementId,
    ],
    [
      segment.ElementOperator.operator_keys.withHistoricalElementId,
      segment.ElementOperator.operator_keys.withHistoricalElementId,
    ],
  ]);
  if (opts.renames) {
    for (const [from, to] of opts.renames) {
      keys.set(from, to);
    }
  }

  const criterionCount =
    opts.eventType && opts.modField ? 3 : opts.eventType || opts.modField ? 2 : 1;
  const criterionValueIndex = criterionCount - 1;

  const baseCriteria: DocumentCriterion[] = [];
  if (opts.eventType) {
    baseCriteria.push({ Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: opts.eventType });
  }
  if (opts.modField) {
    baseCriteria.push({ Field: opts.modField, Op: Op.SEARCH_OP_GT, Value: 0 });
  }

  const matchPartial = (partial: DocumentCriterion[], valueField?: string) => {
    // If we have a field we are matching (like text / css / element id), we will have the full
    // criterion count. Otherwise, we will have one less (like in the any case).
    const partialCriterionCount = valueField ? criterionCount : criterionCount - 1;
    if (partial.length !== partialCriterionCount) {
      return false;
    }
    let idx = 0;
    if (opts.eventType) {
      // first must always match the event type if present, currently not checking op on partials
      if (partial[idx].Field !== FieldEventType || partial[idx].Value !== opts.eventType) {
        return false;
      }
      idx++;
    }
    if (opts.modField) {
      // then must match the mod field if present, currently only checking the field name on partials
      if (partial[idx].Field !== opts.modField) {
        return false;
      }
      idx++;
    }
    if (valueField) {
      // value field must match if present, currently only checking field name on partials
      // value is always last on partials
      if (partial[idx].Field !== valueField) {
        return false;
      }
      idx++;
    }
    return true;
  };

  if (
    baseCriteria.length &&
    (!opts.includes || opts.includes.includes(segment.ElementOperator.operator_keys.any))
  ) {
    // Only when there is base criteria present can we search for "any".
    // Otherwise, the criteria would be empty (like in a dependent case).
    result[keys.get(segment.ElementOperator.operator_keys.any)] = {
      criteria: () => [...baseCriteria],
      withoutPartials: s => stripPartials(s, identity),
      extractFromCriteria: _ => ({
        kind,
        subkind: keys.get(segment.ElementOperator.operator_keys.any),
        value: NONE,
      }),
      extractFromPartialCriteria: c =>
        matchPartial(c) && {
          kind,
          subkind: keys.get(segment.ElementOperator.operator_keys.any),
          value: NONE,
        },
      searchTypeToFilter: st => searchTypeToFilterMirror(st),
      filterToSearchType: sf => filterToSearchTypeMirror(sf),
    };
  }

  if (
    !opts.includes ||
    opts.includes.includes(segment.ElementOperator.operator_keys.withElementId)
  ) {
    result[keys.get(segment.ElementOperator.operator_keys.withElementId)] = {
      criteria: s => [
        ...baseCriteria,
        { Field: NamedElementIdField, Op: Op.SEARCH_OP_EQ, Value: s.value },
      ],
      withoutPartials: s => stripPartials(s, multiNotNullPartial),
      extractFromCriteria: c =>
        c[criterionValueIndex] && {
          kind,
          subkind: keys.get(segment.ElementOperator.operator_keys.withElementId),
          value: c[criterionValueIndex].Value as string[],
        },
      extractFromPartialCriteria: c =>
        matchPartial(c, segment.ElementOperator.operator_keys.withElementId) && {
          kind,
          subkind: keys.get(segment.ElementOperator.operator_keys.withElementId),
          value: [null],
        },
      searchTypeToFilter: st => {
        const ids = st.value.filter(v => v?.type === ElementValueType.Id).map(v => v.value);
        const specs = st.value.filter(v => v?.type === ElementValueType.Spec).map(v => v.value);

        return {
          [st.kind]: {
            [segment.ElementOperator.operator_keys.withElementId]: {
              value: ids,
              spec: specs,
            },
          },
        };
      },
      filterToSearchType: sf => {
        const [kind] = Object.keys(sf);
        const sfValue = sf[kind][segment.ElementOperator.operator_keys.withElementId];

        const ids = sfValue?.value
          ? sfValue?.value?.map(v => ({
              type: ElementValueType.Id,
              value: v,
            }))
          : [];

        const specs = sfValue?.spec
          ? sfValue?.spec?.map(v => ({
              type: ElementValueType.Spec,
              value: v,
            }))
          : [];

        return {
          kind,
          subkind: segment.ElementOperator.operator_keys.withElementId,
          value: [...ids, ...specs] || NONE,
        };
      },
    };
  }

  [
    {
      subkind: segment.ElementOperator.operator_keys.text,
      field: 'EventTargetText',
      op: Op.SEARCH_OP_IS,
    },
    {
      subkind: segment.ElementOperator.operator_keys.textIsNot,
      field: 'EventTargetText',
      op: Op.SEARCH_OP_NEQ,
    },
    {
      subkind: segment.ElementOperator.operator_keys.textStartsWith,
      field: 'EventTargetText',
      op: Op.SEARCH_OP_SW,
    },
    {
      subkind: segment.ElementOperator.operator_keys.textEndsWith,
      field: 'EventTargetText',
      op: Op.SEARCH_OP_EW,
    },
    {
      subkind: segment.ElementOperator.operator_keys.textContainsToken,
      field: 'EventTargetText',
      op: Op.SEARCH_OP_CONTAINS_TOKEN,
    },
    {
      subkind: segment.ElementOperator.operator_keys.textNotContainsToken,
      field: 'EventTargetText',
      op: Op.SEARCH_OP_DOES_NOT_CONTAIN_TOKEN,
    },
    {
      subkind: segment.ElementOperator.operator_keys.textContainsTokenStartsWith,
      field: 'EventTargetText',
      op: Op.SEARCH_OP_CONTAINS_TOKEN_SW,
    },
    {
      subkind: segment.ElementOperator.operator_keys.css,
      field: '$EventTargetSelector',
      op: Op.SEARCH_OP_EQ,
    },
    {
      // don't need field and op for legacy "criteria"
      subkind: segment.ElementOperator.operator_keys.cssIsNot,
    },
    {
      subkind: segment.ElementOperator.operator_keys.withHistoricalElementId,
    },
  ].forEach(f => {
    if (!opts.includes || opts.includes.includes(f.subkind)) {
      result[keys.get(f.subkind)] = {
        criteria: s => [...baseCriteria, { Field: f.field, Op: f.op, Value: s.value }],
        withoutPartials: s => stripPartials(s, multiNotNullPartial),
        extractFromCriteria: c =>
          c[criterionValueIndex] && {
            kind,
            subkind: keys.get(f.subkind),
            value: c[criterionValueIndex].Value as string[],
          },
        extractFromPartialCriteria: c =>
          matchPartial(c, f.field) && {
            kind,
            subkind: keys.get(f.subkind),
            value: [null],
          },
        searchTypeToFilter: st => searchTypeToFilterMirror(st),
        filterToSearchType: sf => filterToSearchTypeMirror(sf),
      };
    }
  });

  [
    segment.ElementOperator.operator_keys.text,
    segment.ElementOperator.operator_keys.textIsNot,
    segment.ElementOperator.operator_keys.textStartsWith,
    segment.ElementOperator.operator_keys.textEndsWith,
    segment.ElementOperator.operator_keys.textContainsToken,
    segment.ElementOperator.operator_keys.textNotContainsToken,
    segment.ElementOperator.operator_keys.textContainsTokenStartsWith,
    segment.ElementOperator.operator_keys.css,
    segment.ElementOperator.operator_keys.cssIsNot,
    segment.ElementOperator.operator_keys.withElementId,
    segment.ElementOperator.operator_keys.withHistoricalElementId,
  ].forEach(key => {
    if (result[keys.get(key)]) {
      result[keys.get(key)] = withValueWrapping(result[keys.get(key)], criterionValueIndex);
    }
  });

  return result;
}

export function apiErrorConverter(kind: EventCustomErrorKinds): ApiErrorClientConverter {
  const result = {} as ApiErrorClientConverter;
  for (const protoOperator of Object.keys(segment.ApiErrorOperator.operator_keys)) {
    result[protoOperator] = {
      filterToSearchType: _ => ({
        kind: UiOnlyKinds.apiError,
        subkind: kind,
        value: protoOperator,
      }),
    };
  }

  return result;
}

export function datalossConverter<T extends SearchType, K extends Kind<T>>(): SubkindConverterMap<
  T,
  K
> {
  const operators: string[] = [
    segment.DatalossOperator.operator_keys.any,
    segment.DatalossOperator.operator_keys.elem,
    segment.DatalossOperator.operator_keys.inputElem,
    segment.DatalossOperator.operator_keys.navUrl,
    segment.DatalossOperator.operator_keys.netUrl,
    segment.DatalossOperator.operator_keys.reqHeader,
    segment.DatalossOperator.operator_keys.rspHeader,
    segment.DatalossOperator.operator_keys.reqBody,
    segment.DatalossOperator.operator_keys.rspBody,
    segment.DatalossOperator.operator_keys.exception,
    segment.DatalossOperator.operator_keys.log,
  ];

  const result = newConverterMap<T, K>();
  for (const operator of operators) {
    result[operator] = {
      withoutPartials: s => stripPartials(s, identity),
      searchTypeToFilter: () => ({ dataloss: { [operator]: {} } }),
      filterToSearchType: () => ({
        kind: segment.EventFilter.filter_keys.dataloss,
        subkind: operator,
        value: NONE,
      }),
      criteria: st => [],
      extractFromCriteria: c => null,
      extractFromPartialCriteria: c => null,
    };
  }

  return result;
}

export function sessionTypeOperatorConverter(): SubkindConverterMap<
  DependentType,
  segment.DependentEventFilter.filter_keys.sessionType
> {
  const result = newConverterMap<
    DependentType,
    segment.DependentEventFilter.filter_keys.sessionType
  >();
  const operators: string[] = [
    segment.SessionTypeOperator.operator_keys.equals,
    segment.SessionTypeOperator.operator_keys.notEquals,
  ];

  for (const protoOperator of operators) {
    result[protoOperator] = {
      withoutPartials: (s: DependentType) =>
        stripPartials(s, (value: SessionType[]) => ({
          ok: value.filter(val => val != SessionType.any).length > 0,
          value: value.filter(val => val != SessionType.any),
        })),
      searchTypeToFilter: (st: SearchType) =>
        searchTypeToFilterMirror(st, forEachValue(uiSessionTypeToProtoSessionType)),
      filterToSearchType: (sf: SearchFilter) =>
        filterToSearchTypeMirror(sf, forEachValue(protoSessionTypeToUiSessionType)),
    };
  }

  return result;
}

export type adjuster<T extends SearchType> = (input: ConverterSpec<T>) => void;

// Adjustment functions that can arbitrarily adjust the converter spec for a subkind
export function adjust<T extends SearchType, K extends Kind<T>>(
  subkindMap: SubkindConverterMap<T, K>,
  ...adjustFuncs: adjuster<T>[]
) {
  for (const adjust of adjustFuncs) {
    for (const value of Object.values(subkindMap)) {
      adjust(value as ConverterSpec<T>);
    }
  }
}

export function adjustCriteria<T extends SearchType>(adjustFunc: CriteriaAdjuster): adjuster<T> {
  return (converterSpec: ConverterSpec<T>) => {
    const original = converterSpec.criteria;
    converterSpec.criteria = v => adjustFunc(original(v));
  };
}

// Conversion helpers
// ------------------
//
// The "to" variants of the conversion functions (e.g., toDate) must follow
// these rules:
//
//   - accept a single parameter of type unknown;
//   - attempt to create a valid return value from the input, otherwise return
//     null; and
//   - never throw (unless it's meant to signal programmer error)
//
//
// The "from" variants of the conversion functions (e.g., fromDate) must follow
// these rules:
//
//   - accept a single parameter that is strongly typed (for instance, fromDate
//     accepts a Date);
//   - return a value that serves as the representation of the input parameter
//     in DocumentCriterion; and
//   - never throw (unless it's meant to signal programmer error)
//

type toFunc<T> = (v: unknown) => T | null;

// If the first argument is an array, mapNonNull maps each element in the array
// using fn. If the mapped values are all non-null, it returns the mapped array.
// Otherwise it returns null.
//
// If the first argument is not an array, it returns null.
export function mapNonNull<T>(v: unknown, fn: toFunc<T>): T[] | null {
  if (v instanceof Array) {
    const mapped = v.map(fn);
    return mapped.every(e => e != null) ? mapped : null;
  }

  return null;
}

// Date
// ----

function isInvalidDate(d: Date): boolean {
  return isNaN(d.getTime());
}

function protoTimeRangeToDates(v: api.TimeRange): [Date, Date] | null {
  if (!v || v.startTime === undefined) {
    return null;
  }

  // NB: In the proto API, we tread date ranges as RNGCO, so we add
  //     a millisecond on the date range -> proto conversion. Remove that
  //     extra millisecond.
  const end = new Date(v.endTime);
  end.setMilliseconds(end.getMilliseconds() - 1);

  return [v.startTime === '' ? null : new Date(v.startTime), v.endTime === '' ? null : end];
}

export function toDate(v: unknown): Date | null {
  if (typeof v != 'string') {
    return null;
  }
  const d = new Date(v);
  return isInvalidDate(d) ? null : d;
}

export function fromDate(v: Date): string {
  return v.toISOString();
}

// is/isNot gets represented as a date range over the wire. This converts the single date selection
// to the expected format.
export function fromSingleDateAsRange(v: Date): [string, string] {
  const inputDate = fromDate(v);
  const dayAfter = new Date(inputDate);
  dayAfter.setDate(dayAfter.getDate() + 1);
  return [inputDate, fromDate(dayAfter)];
}

// Date range
// ----------

// toSingleDateFromRange converts a range that spans exactly 1 day to an single date representation
// which is how it may be represented as a selection in a datepicker such as in is/isNot.
// This function returns null if the value is not of the expected +1 day range.
export function toSingleDateFromRange(v: unknown): Date | null {
  const range = toDateRange(v);
  if (range == null) {
    return null;
  }
  const endCopy = new Date(range[1]);
  endCopy.setDate(endCopy.getDate() - 1);
  if (endCopy.getTime() == range[0].getTime()) {
    return range[0];
  }
  return null;
}

export function toDateRange(v: unknown): [Date, Date] | null {
  if (v instanceof Array && v.length == 2) {
    const a = toDate(v[0]);
    const b = toDate(v[1]);
    if (a != null && b != null) {
      return [a, b];
    }
  }
  return null;
}

export function fromDateRange(v: [Date, Date]): [string, string] {
  return [fromDate(v[0]), fromDate(v[1])];
}

// Duration
// --------

export function toDurationFromSolr(v: unknown): Duration | null {
  if (!(v instanceof Array)) {
    return null;
  }
  if (v.length != 2) {
    return null;
  }
  const [from, to] = v;
  if (typeof from != 'string' || typeof to != 'string') {
    return null;
  }
  if (!isValidSolrTime(from) || !isValidSolrTime(to)) {
    return null;
  }

  const f = parseSolrTime(from);
  const t = parseSolrTime(to);
  if (f.unit != t.unit) {
    // TODO(nishanth): we can do better, but for now we require the units to be the same
    return null;
  }
  return { unit: f.unit, scalar: t.scalar - f.scalar };
}

export function toDuration(v: unknown, units: DurationUnit): Duration | null {
  switch (units) {
    case DurationUnit.second:
      return toDurationFromSec(v);
      break;
    case DurationUnit.milli:
      return toDurationFromMilli(v);
      break;
    default:
      return null;
  }
}

export function toDurationFromMilli(v: unknown, minUnit?: DurationUnit): Duration | null {
  if (typeof v != 'number') {
    return null;
  }
  return fromMilli(v, minUnit);
}

export function toDurationFromSec(v: unknown): Duration | null {
  if (typeof v != 'number') {
    return null;
  }
  return fromSecond(v);
}

// isFuture means that the filter should be forward looking, so we want to reverse the [from, to]
export function fromDurationToSolr(d: Duration, isFuture = false): [string, string] {
  const solrUnit = toSolrUnit(d.unit);

  let from = `NOW/${solrUnit}`;
  // NOTE: we want the right-side of the range to always have a scalar value of 1;
  // e.g., "NOW/MILLI+1MILLI". This behavior is copied from the old search code.
  let to = `NOW/${solrUnit}+1${solrUnit}`;
  if (d.scalar > 1) {
    from = `NOW/${solrUnit}-${d.scalar - 1}${solrUnit}`;
  }

  if (isFuture) {
    from = `NOW/${solrUnit}`;
    to = `NOW/${solrUnit}+${Math.abs(d.scalar)}${solrUnit}`;
  }
  return [from, to];
}

export function fromDuration(d: Duration, units: DurationUnit): number {
  switch (units) {
    case DurationUnit.second:
      return fromDurationToSec(d);
      break;
    case DurationUnit.milli:
      return fromDurationToMilli(d);
      break;
  }
  return 0;
}

export function fromDurationToMilli(d: Duration): number {
  return toMilli(d);
}

export function fromDurationToSec(d: Duration): number {
  return Math.round(toMilli(d) / 1000);
}

export function mostReadableDuration(d: Duration): Duration {
  return toDurationFromMilli(fromDurationToMilli(d));
}

export type partialsFn<T extends SearchType> = (value: any) => { ok: boolean; value: any | any[] };

export function stripPartials<T extends SearchType>(
  s: T,
  withoutPartials: partialsFn<T>,
): { s: T; ok: boolean } {
  if (!s || s.value === null || s.value === undefined) {
    return {
      ok: false,
      s: null,
    };
  }

  const { value, ok } = withoutPartials(s.value);
  return {
    ok,
    s: {
      ...s,
      value,
    } as T,
  };
}

export function notNullPartial<T>(value: T): { ok: boolean; value: T } {
  return {
    ok: value !== null,
    value,
  };
}

/**
 * Returns whether or not all the values in the array are non-null
 * @param value Input SearchType value
 */
export function multiNotNull<T>(value: T[]): { ok: boolean; value: T[] } {
  return {
    ok: value.every(val => val !== null),
    value,
  };
}

export function multiNotNullPartial<T>(value: T[]): { ok: boolean; value: T[] } {
  return {
    ok: value.filter(val => val !== null).length > 0,
    value: value.filter(val => val !== null),
  };
}

export function durationPartial<T>(value: T): { ok: boolean; value: T } {
  if (isDurationValue(value)) {
    return {
      ok: value.scalar != null,
      value,
    };
  }
  return {
    ok: false,
    value,
  };
}

export function multiDurationPartial(value: Duration[]): { ok: boolean; value: Duration[] } {
  return {
    ok: value.filter(val => val.scalar != null).length > 0,
    value: value.filter(val => val.scalar != null),
  };
}

export function identity<T>(value: T): { ok: true; value: T } {
  return {
    ok: true,
    value,
  };
}

export const referrerUrlCategoryToField: Map<UrlCategory, string> = new Map([
  [UrlCategory.full, 'PageRefererUrl'],
  [UrlCategory.fragment, 'PageRefererUrlHash'],
  [UrlCategory.host, 'PageRefererUrlHost'],
  [UrlCategory.path, 'PageRefererUrlPath'],
  [UrlCategory.new_path, 'PageRefererUrlPathStr'],
  [UrlCategory.query, 'PageRefererUrlQuery'],
]);

export const fieldToReferrerUrlCategory: Map<string, UrlCategory> = new Map([
  ['PageRefererUrl', UrlCategory.full],
  ['PageRefererUrlHash', UrlCategory.fragment],
  ['PageRefererUrlHost', UrlCategory.host],
  ['PageRefererUrlPath', UrlCategory.path],
  ['PageRefererUrlPathStr', UrlCategory.new_path],
  ['PageRefererUrlQuery', UrlCategory.query],
]);

export const visitedUrlCategoryToField: Map<UrlCategory, string> = new Map([
  [UrlCategory.full, 'PageUrl'],
  [UrlCategory.fragment, 'PageUrlHash'],
  [UrlCategory.host, 'PageUrlHost'],
  [UrlCategory.path, 'PageUrlPath'],
  [UrlCategory.new_path, 'PageUrlPathStr'],
  [UrlCategory.query, 'PageUrlQuery'],
]);

export const pageRefreshUrlCategoryToField: Map<UrlCategory, string> = new Map([
  [UrlCategory.full, 'PageRefreshPageUrl'],
  [UrlCategory.fragment, 'PageRefreshPageUrlHash'],
  [UrlCategory.host, 'PageRefreshPageUrlHost'],
  [UrlCategory.path, 'PageRefreshPageUrlPath'],
  [UrlCategory.path, 'PageRefreshPageUrlPathStr'],
  [UrlCategory.query, 'PageRefreshPageUrlQuery'],
]);

export const fieldToVisitedUrlCategory: Map<string, UrlCategory> = new Map([
  ['PageUrl', UrlCategory.full],
  ['PageUrlHash', UrlCategory.fragment],
  ['PageUrlHost', UrlCategory.host],
  ['PageUrlPath', UrlCategory.path],
  ['PageUrlPathStr', UrlCategory.new_path],
  ['PageUrlQuery', UrlCategory.query],
]);

export const fieldToPageRefreshUrlCategory: Map<string, UrlCategory> = new Map([
  ['PageRefreshPageUrl', UrlCategory.full],
  ['PageRefreshPageUrlHash', UrlCategory.fragment],
  ['PageRefreshPageUrlHost', UrlCategory.host],
  ['PageRefreshPageUrlPath', UrlCategory.path],
  ['PageRefreshPageUrlPathStr', UrlCategory.new_path],
  ['PageRefreshPageUrlQuery', UrlCategory.query],
]);

export const ajaxUrlCategoryToField: Map<UrlCategory, string> = new Map([
  [UrlCategory.full, 'ReqUrl'],
  [UrlCategory.host, 'ReqUrlHost'],
  [UrlCategory.path, 'ReqUrlPath'],
  [UrlCategory.new_path, 'ReqUrlPathStr'],
  [UrlCategory.query, 'ReqUrlQuery'],
]);

export const fieldToAjaxUrlCategory: Map<string, UrlCategory> = new Map([
  ['ReqUrl', UrlCategory.full],
  ['ReqUrlHost', UrlCategory.host],
  ['ReqUrlPath', UrlCategory.path],
  ['ReqUrlPathStr', UrlCategory.new_path],
  ['ReqUrlQuery', UrlCategory.query],
]);

export const webSourceFileUrlCategoryToField: Map<UrlCategory, string> = new Map([
  [UrlCategory.full, 'EventWebSourceFileUrl'],
  [UrlCategory.fragment, 'EventWebSourceFileUrlHash'],
  [UrlCategory.host, 'EventWebSourceFileUrlHost'],
  [UrlCategory.path, 'EventWebSourceFileUrlPath'],
  [UrlCategory.new_path, 'EventWebSourceFileUrlPathStr'],
  [UrlCategory.query, 'EventWebSourceFileUrlQuery'],
]);

export const fieldToWebSourceFileUrlCategory: Map<string, UrlCategory> = new Map([
  ['EventWebSourceFileUrl', UrlCategory.full],
  ['EventWebSourceFileUrlHash', UrlCategory.fragment],
  ['EventWebSourceFileUrlHost', UrlCategory.host],
  ['EventWebSourceFileUrlPath', UrlCategory.path],
  ['EventWebSourceFileUrlPathStr', UrlCategory.new_path],
  ['EventWebSourceFileUrlQuery', UrlCategory.query],
]);

export const fieldToUrlCategory = new Map<string, UrlCategory>([
  ...fieldToReferrerUrlCategory,
  ...fieldToVisitedUrlCategory,
  ...fieldToPageRefreshUrlCategory,
  ...fieldToAjaxUrlCategory,
  ...fieldToWebSourceFileUrlCategory,
]);

export function dateConverter<T extends SearchType, K extends Kind<T>>(kind: K, field: string) {
  const result = {
    [UiOnlySubkinds.isDateLike]: {
      criteria: s => [
        { Field: field, Op: Op.SEARCH_OP_RNGCO, Value: fromSingleDateAsRange(s.value) },
      ],
      withoutPartials: s => stripPartials(s, notNullPartial),
      extractFromCriteria: c =>
        c[0] &&
        mapNonNull(c[0].Value, toDate) && {
          kind,
          subkind: UiOnlySubkinds.isDateLike as const,
          value: toDate(c[0].Value[0]),
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        // Translate from SEARCH_OP_EQ because the old UI would use SEARCH_OP_EQ as the representation and then translate to SEARCH_OP_RNGCO on request.
        (c[0].Op == Op.SEARCH_OP_EQ || c[0].Op == Op.SEARCH_OP_RNGCO) &&
        c[0].Field == field && {
          kind,
          subkind: UiOnlySubkinds.isDateLike as const,
          value: null,
        },
      searchTypeToFilter: st => {
        const range = fromSingleDateAsRange(st.value);
        return {
          [kind]: {
            range: {
              value: {
                startTime: range[0],
                endTime: range[1],
              },
            },
          },
        };
      },
      filterToSearchType: () => null, // UI-only.
    },
    [segment.DateOperator.operator_keys.after]: {
      criteria: s => [{ Field: field, Op: Op.SEARCH_OP_GT, Value: fromDate(s.value) }],
      withoutPartials: s => stripPartials(s, notNullPartial),
      extractFromCriteria: c =>
        c[0] &&
        toDate(c[0].Value) && {
          kind,
          subkind: segment.DateOperator.operator_keys
            .after as segment.DateOperator.operator_keys.after,
          value: toDate(c[0].Value),
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == field &&
        c[0].Op == Op.SEARCH_OP_GT && {
          kind,
          subkind: segment.DateOperator.operator_keys
            .after as segment.DateOperator.operator_keys.after,
          value: null,
        },
      searchTypeToFilter: searchTypeToFilterDateValue,
      filterToSearchType: filterToSearchTypeDateValue,
    },
    [segment.DateOperator.operator_keys.before]: {
      criteria: s => [{ Field: field, Op: Op.SEARCH_OP_LT, Value: fromDate(s.value) }],
      withoutPartials: s => stripPartials(s, notNullPartial),
      extractFromCriteria: c =>
        c[0] &&
        toDate(c[0].Value) && {
          kind,
          subkind: segment.DateOperator.operator_keys
            .before as segment.DateOperator.operator_keys.before,
          value: toDate(c[0].Value),
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == field &&
        c[0].Op == Op.SEARCH_OP_LT && {
          kind,
          subkind: segment.DateOperator.operator_keys
            .before as segment.DateOperator.operator_keys.before,
          value: null,
        },
      searchTypeToFilter: searchTypeToFilterDateValue,
      filterToSearchType: filterToSearchTypeDateValue,
    },
    [segment.DateOperator.operator_keys.range]: {
      criteria: s => [{ Field: field, Op: Op.SEARCH_OP_RNGCC, Value: fromDateRange(s.value) }],
      withoutPartials: s =>
        stripPartials(s, value => ({
          ok: value[0] !== null && value[1] !== null,
          value,
        })),
      extractFromCriteria: c =>
        c[0] &&
        toDateRange(c[0].Value) && {
          kind,
          subkind: segment.DateOperator.operator_keys
            .range as segment.DateOperator.operator_keys.range,
          value: toDateRange(c[0].Value),
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == field &&
        c[0].Op == Op.SEARCH_OP_RNGCC && {
          kind,
          subkind: segment.DateOperator.operator_keys
            .range as segment.DateOperator.operator_keys.range,
          value: [null, null] as [Date, Date],
        },
      searchTypeToFilter: searchTypeToFilterDateRangeValue,
      filterToSearchType: sf => {
        const value: api.TimeRange =
          sf[kind as keyof SearchFilter][segment.CustomFieldDateOperator.operator_keys.range].value;
        switch (true) {
          case toSingleDateFromRange([value.startTime, value.endTime]) != null:
            return {
              kind,
              subkind: UiOnlySubkinds.isDateLike as const,
              value: toSingleDateFromRange([value.startTime, value.endTime]),
            };
          case toDurationFromSolr([value.startTime, value.endTime]) != null:
            return {
              kind,
              subkind: UiOnlySubkinds.lastDurationLike as const,
              value: toDurationFromSolr([value.startTime, value.endTime]),
            };
          default:
            return filterToSearchTypeDateRangeValue(sf);
        }
      },
    },
    [segment.DateOperator.operator_keys.notRange]: {
      criteria: s => [{ Field: field, Op: Op.SEARCH_OP_NOT_RNGCC, Value: fromDateRange(s.value) }],
      withoutPartials: s =>
        stripPartials(s, value => ({
          ok: value[0] !== null && value[1] !== null,
          value,
        })),
      extractFromCriteria: c =>
        c[0] &&
        toDateRange(c[0].Value) && {
          kind,
          subkind: segment.DateOperator.operator_keys
            .notRange as segment.DateOperator.operator_keys.notRange,
          value: toDateRange(c[0].Value),
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == field &&
        c[0].Op == Op.SEARCH_OP_NOT_RNGCC && {
          kind,
          subkind: segment.DateOperator.operator_keys
            .notRange as segment.DateOperator.operator_keys.notRange,
          value: [null, null] as [Date, Date],
        },
      searchTypeToFilter: searchTypeToFilterDateRangeValue,
      filterToSearchType: sf => {
        const value: api.TimeRange =
          sf[kind as keyof SearchFilter][segment.CustomFieldDateOperator.operator_keys.notRange]
            .value;
        switch (true) {
          case toDurationFromSolr([value.startTime, value.endTime]) != null:
            return {
              kind,
              subkind: UiOnlySubkinds.notLastDurationLike as const,
              value: toDurationFromSolr([value.startTime, value.endTime]),
            };
          default:
            return filterToSearchTypeDateRangeValue(sf);
        }
      },
    },
    [UiOnlySubkinds.lastDurationLike]: {
      criteria: s => [{ Field: field, Op: Op.SEARCH_OP_RNGCC, Value: fromDurationToSolr(s.value) }],
      withoutPartials: s => stripPartials(s, durationPartial),
      extractFromCriteria: c =>
        c[0] &&
        toDurationFromSolr(c[0].Value) && {
          kind,
          subkind: UiOnlySubkinds.lastDurationLike as const,
          value: toDurationFromSolr(c[0].Value),
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == field &&
        c[0].Op == Op.SEARCH_OP_RNGCC && {
          kind,
          subkind: UiOnlySubkinds.lastDurationLike as const,
          value: null,
        },
      searchTypeToFilter: st => {
        const solrRelativeTime = fromDurationToSolr(st.value);
        const range: api.TimeRange = {
          startTime: solrRelativeTime[0],
          endTime: solrRelativeTime[1],
        };
        return {
          [kind]: {
            range: {
              value: range,
            },
          },
        };
      },
      filterToSearchType: () => null, // UI-only.
    },
    [UiOnlySubkinds.notLastDurationLike]: {
      criteria: s => [
        { Field: field, Op: Op.SEARCH_OP_NOT_RNGCC, Value: fromDurationToSolr(s.value) },
      ],
      withoutPartials: s => stripPartials(s, durationPartial),
      extractFromCriteria: c =>
        c[0] &&
        toDurationFromSolr(c[0].Value) && {
          kind,
          subkind: UiOnlySubkinds.notLastDurationLike as const,
          value: toDurationFromSolr(c[0].Value),
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == field &&
        c[0].Op == Op.SEARCH_OP_NOT_RNGCC && {
          kind,
          subkind: UiOnlySubkinds.notLastDurationLike as const,
          value: null,
        },
      searchTypeToFilter: st => {
        const solrRelativeTime = fromDurationToSolr(st.value);
        const range: api.TimeRange = {
          startTime: solrRelativeTime[0],
          endTime: solrRelativeTime[1],
        };
        return {
          [kind]: {
            notRange: {
              value: range,
            },
          },
        };
      },
      filterToSearchType: null, // UI-only.
    },
  };

  for (const k of Object.keys(pick(result, UiOnlySubkinds.isDateLike))) {
    result[k] = withValueWrapping(result[k], 0);
  }

  return result;
}
